import api, { API_BASE_URL } from '@config/api';
import type {
  File,
  Comment,
  FileQueryParams,
  UploadUrlResponse,
  DownloadUrlResponse,
  CreateUploadUrlDto,
} from '../types';
import type { PaginatedResponse } from '@common/types/api';

export const fileService = {
  getFiles: async (params: FileQueryParams): Promise<PaginatedResponse<File>> => {
    const response = await api.get('/files', { params });
    return response.data;
  },

  getFile: async (id: string): Promise<File> => {
    const response = await api.get(`/files/${id}`);
    return response.data;
  },

  getUploadUrl: async (data: CreateUploadUrlDto): Promise<UploadUrlResponse> => {
    const response = await api.post('/files/upload-url', data);
    return response.data;
  },

  confirmUpload: async (id: string, duration?: number): Promise<File> => {
    const response = await api.post(`/files/${id}/confirm`, { duration });
    return response.data;
  },

  getDownloadUrl: async (id: string): Promise<DownloadUrlResponse> => {
    const response = await api.get(`/files/${id}/download`);
    return response.data;
  },

  /**
   * URL absoluta para baixar N arquivos como ZIP streamed. Para usar com
   * `<a href download>` — o cookie HTTP-only de autenticação é enviado
   * automaticamente pelo navegador (GET não exige CSRF).
   */
  getBulkDownloadUrl: (fileIds: string[]): string => {
    const ids = encodeURIComponent(fileIds.join(','));
    return `${API_BASE_URL}/files/bulk-download?ids=${ids}`;
  },

  /** URL absoluta para baixar uma pasta inteira como ZIP streamed. */
  getFolderDownloadUrl: (folderId: string): string => {
    return `${API_BASE_URL}/folders/${encodeURIComponent(folderId)}/download`;
  },

  /** URL absoluta para stream do conteúdo do arquivo via proxy autenticado. */
  getStreamProxyUrl: (fileId: string): string => {
    return `${API_BASE_URL}/files/${fileId}/stream-proxy`;
  },

  updateFile: async (id: string, data: { name?: string; description?: string; tags?: string[] }): Promise<File> => {
    const response = await api.patch(`/files/${id}`, data);
    return response.data;
  },

  deleteFile: async (id: string): Promise<void> => {
    await api.delete(`/files/${id}`);
  },

  getComments: async (fileId: string): Promise<Comment[]> => {
    const response = await api.get(`/files/${fileId}/comments`);
    return response.data;
  },

  createComment: async (fileId: string, content: string): Promise<Comment> => {
    const response = await api.post(`/files/${fileId}/comments`, { content });
    return response.data;
  },

  deleteComment: async (fileId: string, commentId: string): Promise<void> => {
    await api.delete(`/files/${fileId}/comments/${commentId}`);
  },

  getSharedFiles: async (params?: FileQueryParams): Promise<PaginatedResponse<File>> => {
    const response = await api.get('/files/shared-with-me', { params });
    return response.data;
  },
};
